import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import SciFiLoader from '@/components/ui/SciFiLoader';
import { resolvePostAuthRoute } from '@/lib/post-auth-route';
import { NATIVE_POST_AUTH_PENDING_KEY } from '@/hooks/use-native-app';

interface PostAuthRedirectProps {
  userId: string;
}

const PostAuthRedirect = ({ userId }: PostAuthRedirectProps) => {
  const location = useLocation();
  const [target, setTarget] = useState<string | null>(null);

  useEffect(() => {
    let isActive = true;
    // Signal to Home.tsx that a post-auth resolution is in progress so it
    // doesn't race with its own onboarding redirect.
    try { sessionStorage.setItem('post_auth_resolving', '1'); } catch {}

    const resolveTarget = async () => {
      try {
        const inviteCode = new URLSearchParams(location.search).get('invite') || '';
        const result = await resolvePostAuthRoute(userId, inviteCode);
        if (isActive) setTarget(result.target);
      } catch {
        // On error default to onboarding — correct for new users (the
        // common case) and harmless for existing users (they'll be
        // redirected to home by Onboarding's own completed-check).
        if (isActive) setTarget('/onboarding');
      } finally {
        try {
          sessionStorage.removeItem('post_auth_resolving');
          sessionStorage.removeItem(NATIVE_POST_AUTH_PENDING_KEY);
        } catch {}
      }
    };

    void resolveTarget();

    return () => {
      isActive = false;
    };
  }, [location.search, userId]);

  if (!target) return <SciFiLoader fullscreen />;

  return <Navigate to={target} replace />;
};

export default PostAuthRedirect;